<template>
  <div v-if="showModal" class="bet-confirmation-modal" @click.self="closeModal">
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h2 class="modal-title">Bet Taken</h2>
          <div class="close-btn" @click="closeModal">
            <img
              src="/icons/close.svg"
              alt="close"
              class="close-icon day-mode"
            />
            <img
              src="/icons/close-night.svg"
              alt="close"
              class="close-icon night-mode"
            />
          </div>
        </div>
      </div>

      <!-- Receipt Content -->
      <div class="receipt-container">
        <div class="receipt">
          <!-- Receipt Header -->
          <div class="receipt-header">
            <h3 class="receipt-title">RECEIPT</h3>
          </div>

          <!-- Bet Details -->
          <div class="bet-details">
            <div class="detail-row">
              <span class="detail-label">Bet Type:</span>
              <span class="detail-value">{{ betTypeDisplay }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Game:</span>
              <span class="detail-value-info">{{ betInfo.gameInfo }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Selection:</span>
              <span class="detail-value">{{ betInfo.sideString }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Odds:</span>
              <span class="detail-value">{{ betInfo.odds }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Amount:</span>
              <span class="detail-value">${{ formattedAmount }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="modal-actions">
        <button class="continue-btn" @click="closeModal">Acknowledge</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useUserStore } from '@/stores/user';

const userStore = useUserStore();
const showModal = ref(false);
const betInfo = ref({});

const formattedAmount = computed(() => {
  if (!betInfo.value.amount) return '0.00';
  return Number(betInfo.value.amount).toFixed(2);
});

const betTypeDisplay = computed(() => {
  if (!betInfo.value.betType) return 'Moneyline';
  const type = betInfo.value.betType.toLowerCase();
  switch (type) {
    case 'total':
      return 'Total';
    case 'spread':
      return 'Spread';
    case 'moneyline':
      return 'Moneyline';
    default:
      return 'Moneyline';
  }
});

function show(betData) {
  betInfo.value = betData;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  betInfo.value = {};
}

// Expose the show function to parent components
defineExpose({
  show,
  closeModal,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.bet-confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: main.$black-202;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050; // above the exchange offers dock, under the base modal wrapper
  padding: 1rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    padding: 0.5rem;
  }
}

.modal-content {
  background: main.$white_01;
  border-radius: 12px;
  box-shadow: 0 4px 20px main.$black-201;
  width: 100%;
  max-width: 450px;
  overflow: hidden;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    max-width: 100%;
    border-radius: 8px;
  }
}

.modal-header {
  background: main.$color-primary_02;
  padding: 1rem 1.5rem;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-title {
    color: main.$white;
    font-size: 1.25rem;
    font-weight: main.$font-weight-bold;
    margin: 0;
    font-family: 'Poppins Bold', Arial, sans-serif;
  }

  .close-btn {
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: background-color 0.2s ease;

    &:hover {
      background: main.$black-103;
    }

    .close-icon {
      width: 20px;
      height: 20px;
    }
  }
}

.receipt-container {
  padding: 1.5rem;
  background: main.$white;
}

.receipt {
  background: main.$white-200;
  border-radius: 8px;
  padding: 1.5rem;
  border: 2px solid main.$grey-402;
  box-shadow: inset 0 2px 4px main.$black-105;

  .receipt-header {
    text-align: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid main.$grey-402;

    .receipt-title {
      color: main.$black;
      font-size: 1.125rem;
      font-weight: main.$font-weight-bold;
      margin: 0;
      letter-spacing: 1px;
      font-family: 'Poppins Bold', Arial, sans-serif;
    }
  }

  .bet-details {
    margin-bottom: 1.5rem;

    .detail-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid main.$grey-402;

      &:last-child {
        border-bottom: none;
      }

      .detail-label {
        color: main.$black-203;
        font-weight: main.$font-weight-bold;
        font-size: 0.875rem;
        font-family: 'Poppins Medium', Arial, sans-serif;
      }

      .detail-value {
        color: main.$black;
        font-weight: main.$font-weight-bold;
        font-size: 0.875rem;
        text-align: right;
        font-family: 'Poppins Bold', Arial, sans-serif;
      }

      .detail-value-info {
        color: main.$black;
        font-weight: main.$font-weight-bold;
        font-size: 0.75rem;
        text-align: right;
        font-family: 'Poppins Bold', Arial, sans-serif;
      }
    }
  }
}

.modal-actions {
  padding: 1.5rem;
  background: main.$white_01;
  display: flex;
  justify-content: center;

  .continue-btn {
    background: main.$color-primary;
    color: main.$white;
    border: none;
    border-radius: 8px;
    padding: 0.875rem 2rem;
    font-size: 1rem;
    font-weight: main.$font-weight-bold;
    font-family: 'Poppins Bold', Arial, sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 120px;

    &:hover {
      background: main.$color-primary_04;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px main.$black-103;
    }

    &:active {
      transform: translateY(0);
    }

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      padding: 1rem 1.5rem;
      font-size: 0.875rem;
    }
  }
}

// Theme-specific styles
[theme='night'] {
  .modal-content {
    background: main.$white_01; // Dark background for night mode
  }

  .modal-header {
    background: main.$color-primary_02; // Dark header
  }

  .receipt-container {
    background: main.$white_01; // Dark container background
  }

  .receipt {
    background: main.$white_07; // Dark receipt background (#3A4266)
    border-color: main.$grey-400; // Dark border (#3A4266)
    box-shadow: inset 0 2px 4px main.$black-104; // Dark shadow

    .receipt-header {
      border-bottom-color: main.$grey-400; // Dark border

      .receipt-title {
        color: main.$black; // Light text (#f5f5f5)
      }
    }

    .bet-details {
      .detail-row {
        border-bottom-color: main.$grey-400; // Dark border

        .detail-label {
          color: main.$black-203_01; // Light grey text (#c5c8e6)
        }

        .detail-value {
          color: main.$black; // Light text (#f5f5f5)
        }

        .detail-value-info {
          color: main.$black; // Light text (#f5f5f5)
          font-weight: main.$font-weight-bold;
          font-size: 0.75rem;
          text-align: right;
          font-family: 'Poppins Bold', Arial, sans-serif;
        }
      }
    }
  }

  .modal-actions {
    background: main.$white_01; // Dark actions background

    .continue-btn {
      background: main.$color-primary_04; // Dark blue button (#333B86)
      color: main.$black; // Light text

      &:hover {
        background: main.$color-primary_07; // Lighter blue on hover (#5A64A0)
        box-shadow: 0 4px 12px main.$black-104; // Dark shadow
      }
    }
  }

  .close-btn {
    .day-mode {
      display: none;
    }

    .night-mode {
      display: block;
    }
  }
}

[theme='day'] {
  .close-btn {
    .day-mode {
      display: block;
    }

    .night-mode {
      display: none;
    }
  }
}
</style>
